"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
} from "recharts";
import { Clock, ArrowLeftRight, ShieldCheck } from "lucide-react";
import { DonutCard, SliceData, COLORS } from "@/components/charts/NeonDonut";
import { formatCurrency } from "@/lib/formatters";
import { PortfolioPosition } from "@/lib/api/portfolio";
import { Activity } from "@/lib/api/activities";
import { RealEstateProperty } from "@/lib/api/properties";

interface Props {
  baseCurrency: string;
  liquid: number;
  holdings: PortfolioPosition[];
  activities: Activity[];
  properties: RealEstateProperty[];
  includeProperties: boolean;
  isLoading: boolean;
  mounted: boolean;
}

export function PortfolioDistributionSection({
  baseCurrency,
  liquid,
  holdings,
  activities,
  properties,
  includeProperties,
  mounted,
}: Props) {
  const t = useTranslations("Overview");

  const donutData = useMemo<SliceData[]>(() => {
    const cryptoValue = holdings
      .filter(h => {
        const sub = h.asset_sub_class?.toUpperCase();
        const cls = h.asset_class?.toUpperCase();
        return sub === "CRYPTOCURRENCY" || cls === "ALTERNATIVE_INVESTMENT";
      })
      .reduce((sum, h) => sum + h.value_in_base_currency, 0);

    const capitalValue = holdings
      .filter(h => {
        const sub = h.asset_sub_class?.toUpperCase();
        const cls = h.asset_class?.toUpperCase();
        return sub !== "CRYPTOCURRENCY" && cls !== "ALTERNATIVE_INVESTMENT";
      })
      .reduce((sum, h) => sum + h.value_in_base_currency, 0);

    const propertiesTotal = includeProperties
      ? properties.reduce((sum, p) => sum + p.display_value_base_currency, 0)
      : 0;

    const total = liquid + cryptoValue + capitalValue + propertiesTotal;

    const baseSlices = [
      { name: t("liquid"),           value: liquid,       currency: baseCurrency, color: COLORS[0] },
      { name: t("cryptocurrencies"), value: cryptoValue,  currency: baseCurrency, color: COLORS[1] },
      { name: t("investedCapital"),  value: capitalValue, currency: baseCurrency, color: COLORS[2] },
    ].filter(s => s.value > 0);

    const propertySlices = includeProperties
      ? properties
          .filter(p => p.display_value_base_currency > 0)
          .map((p, i) => ({
            name: p.name,
            value: p.display_value_base_currency,
            currency: baseCurrency,
            color: COLORS[(3 + i) % COLORS.length],
          }))
      : [];

    return [...baseSlices, ...propertySlices].map(s => ({
      ...s,
      percentage: total > 0 ? (s.value / total) * 100 : 0,
    }));
  }, [holdings, liquid, properties, includeProperties, baseCurrency, t]);

  const { yearsInMarket, sparklineData, activitiesCount } = useMemo(() => {
    const sorted = [...activities].sort((a, b) => a.date.localeCompare(b.date));
    const firstDate = sorted[0]?.date;
    const years = firstDate
      ? (Date.now() - new Date(firstDate).getTime()) / (365.25 * 24 * 3600 * 1000)
      : 0;

    const monthCounts = activities.reduce((acc, a) => {
      const key = a.date.substring(0, 7);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const sparkline = Object.entries(monthCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, count]) => ({ count }));

    return { yearsInMarket: years, sparklineData: sparkline, activitiesCount: activities.length };
  }, [activities]);

  const { buyTotal, sellTotal } = useMemo(() => {
    // Values are summed in native activity currency — approximation for v1
    const buy = activities
      .filter(a => a.type === "BUY")
      .reduce((sum, a) => sum + a.quantity * a.unit_price, 0);
    const sell = activities
      .filter(a => a.type === "SELL")
      .reduce((sum, a) => sum + a.quantity * a.unit_price, 0);
    return { buyTotal: buy, sellTotal: sell };
  }, [activities]);

  const yearsFloor = Math.floor(yearsInMarket);
  const monthsRound = Math.round(yearsInMarket * 12);
  const durationText = yearsInMarket >= 1
    ? (yearsFloor === 1
        ? t("timeInMarketAboutYear", { count: 1 })
        : t("timeInMarketAboutYears", { count: yearsFloor }))
    : (monthsRound === 1
        ? t("timeInMarketMonth", { count: 1 })
        : t("timeInMarketMonths", { count: monthsRound }));

  const maxBuySell = Math.max(buyTotal, sellTotal, 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Left: donut chart */}
      <DonutCard
        title={t("portfolioDistribution")}
        data={donutData}
        mounted={mounted}
      />

      {/* Right: 3 cards filling the same height as the donut card */}
      <div className="flex flex-col gap-3">
        {/* 40% — Buys and sells */}
        <div className="bg-surface rounded-xl p-5 flex flex-col" style={{ flex: 4 }}>
          <div className="flex items-center gap-2 mb-3 shrink-0">
            <ArrowLeftRight className="h-4 w-4 text-muted shrink-0" />
            <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">
              {t("buysAndSells")}
            </p>
          </div>
          <div className="flex gap-4 flex-1 min-h-0">
            {/* Text */}
            <div className="flex-1 flex flex-col justify-center gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-green font-semibold">
                  {t("buy")}
                </span>
                <span className="text-green font-bold tabular-nums text-sm">
                  {formatCurrency(buyTotal, baseCurrency)}
                </span>
              </div>
              <div className="border-t border-border" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-red-500 font-semibold">
                  {t("sell")}
                </span>
                <span className="text-red-500 font-bold tabular-nums text-sm">
                  {formatCurrency(sellTotal, baseCurrency)}
                </span>
              </div>
            </div>
            {/* Bar chart */}
            {(buyTotal > 0 || sellTotal > 0) && (
              <div className="flex items-end gap-1.5 shrink-0 self-stretch">
                {buyTotal > 0 && (
                  <div
                    className="w-2.5 rounded-t-sm bg-green"
                    style={{ height: `${Math.round((buyTotal / maxBuySell) * 100)}%` }}
                  />
                )}
                {sellTotal > 0 && (
                  <div
                    className="w-2.5 rounded-t-sm bg-red-500"
                    style={{ height: `${Math.round((sellTotal / maxBuySell) * 100)}%` }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* 30% — Time in market */}
        <div className="bg-surface rounded-xl p-5 flex flex-col justify-center" style={{ flex: 3 }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-sky-400 shrink-0" />
                <p className="text-[10px] uppercase tracking-widest text-sky-400/70 font-semibold truncate">
                  {t("timeInMarket").toUpperCase()}: {durationText.toUpperCase()}
                </p>
              </div>
              <p className="text-2xl font-bold text-text tabular-nums">
                {activitiesCount.toLocaleString()}{" "}
                <span className="text-sm font-normal text-muted">{t("activitiesLabel")}</span>
              </p>
            </div>
            {mounted && sparklineData.length > 1 && (
              <div className="shrink-0 w-28 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#38BDF8"
                      strokeWidth={1.5}
                      fill="url(#sparkGrad)"
                      dot={false}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* 30% — Emergency fund (placeholder) */}
        <div className="bg-surface rounded-xl p-5 flex flex-col justify-center" style={{ flex: 3 }}>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-green shrink-0" />
            <p className="text-[10px] uppercase tracking-widest text-green/70 font-semibold">
              {t("emergencyFund")}
            </p>
          </div>
          <p className="text-2xl font-bold text-muted tabular-nums">
            {formatCurrency(0, baseCurrency)}
          </p>
        </div>
      </div>
    </div>
  );
}
